// In-scene camera sticker scanner (Echo-adapted port of Sweet Tooth's Video).
//
// This is the SceneObject that renders the live camera feed and drives the real
// GPU sticker-decoding pipeline. It follows Sweet Tooth's design: it lives in
// the active scene, is inactive until the scan flow turns it on, brings up the
// real camera on activation, runs StickerCompute (the full black-white / blob /
// 3x3 decode pipeline) each frame, feeds decoded codes through a StableCodeGate,
// and on a stable code reports the result and tears itself down.
//
// It renders the full Sweet Tooth visual/debug surface: a warm-tinted scrim
// backdrop (drawn first, owned by Video — there is no separate Scrim object), a
// YUV→RGB camera feed (NV12 / I420), a debug mask overlay, a blob-overlay
// pipeline, and an always-on scan-feedback overlay, switchable via
// ui_message("render", ...).
//
// Activation is message-driven and deterministic:
//   - "scan_start" → activate()   (idempotent; no duplicate camera/instance)
//   - "scan_stop"  → deactivate() (idempotent)
//   - "scan_sticker" → toggle     (Sweet Tooth parity)
//   - "render" (color | black and white | blobs) → show_mode
// The engine's SceneScanController sends scan_start/scan_stop via
// scene_service.ui_message, so the Scan button (StickerService.scan) activates
// this object in whichever scene is current.
//
// Results flow back through the engine ScanBridge (report_scanned / report_error),
// which the sticker adapter awaits — Video never imports the sticker adapter, and
// the adapter never imports Video. This is the deliberate Echo Engine deviation
// from Sweet Tooth, which instead re-emits the decoded code back onto the scene
// bus (a sticker message plus a scan-toggle message). The scan lifecycle
// (start/stop/cancel) and the StickerService adapter boundary are preserved;
// only the reporting hop differs.

import SceneObject from '../../../lib/services/scene/sceneobject';
import ShaderService from '../../../lib/services/shader/shader_service';
import ResourceService from '../../../lib/services/resource/resourceservice';
import Camera from '../../../lib/services/scene/camera';
import Stage from '../../../lib/services/stage/stage';
import { scanBridge } from '../../../lib/services/scene/scan_bridge';
// Import StickerError from the leaf types module (not the public sticker_service)
// so StickerService can own/instantiate Video without a circular import.
import { StickerError } from '../../../lib/services/sticker/sticker_types';
import CaptureService from '../../../lib/services/capture/capture_service';
import RenderService from '../../../lib/services/render/render_service';
import StickerCompute from '../../../compute/sticker_compute';
import StickerInfoGpu3x3 from '../../../compute/stickerinfogpu3x3';
import VideoRenderPipeline from './video_renderpipeline';
import DebugTextureRenderPipeline from './debug_texture_renderpipeline';
import DebugBlobOverlayPipeline from './debug_blob_overlay_renderpipeline';
import ScanFeedbackPipeline from './scan_feedback_renderpipline';
import ScrimRenderPipeline from './scrim_renderpipeline';
import StickerDebug from './sticker_debug';
import { Keys } from '../../../lib/services/input/inputservice';
import {
  StableCodeGate,
  CodeGateResult,
  type CodeGateCandidateStats,
} from './stable_code_gate';
import { buildAcceptedStickerDiagnosticLog } from './sticker_acceptance_diagnostics';
import { VideoDisplayInfo } from './video_display_helper';
import { rb_log, rb_debug_log, rb_throttle } from '../../../lib/services/compute/rb_log';

const MAX_STICKERS = 256;
const STICKER_TYPE_INVALID = 0;
const STICKER_TYPE_3X3 = 3;

// Footer slot used for scan feedback (mirrors the demo overlay slot).
const SCAN_FOOTER_SLOT = 1;

export default class Video extends SceneObject {
  private pipeline_nv12: VideoRenderPipeline;
  private pipeline_i420: VideoRenderPipeline;
  private debug_pipeline: DebugTextureRenderPipeline;
  private blob_overlay_nv12: DebugBlobOverlayPipeline;
  private blob_overlay_i420: DebugBlobOverlayPipeline;
  private scan_feedback_pipeline: ScanFeedbackPipeline;
  private show_mode: 'video' | 'mask' | 'blob_overlay' = 'video';

  // Scrim: a warm-tinted dim backdrop drawn BEFORE the camera feed and overlays.
  // Video owns it directly (there is no separate Scrim SceneObject). It fades in
  // while the scanner is active and fades back out on deactivate.
  private scrim_pipeline: ScrimRenderPipeline;
  private scrim_alpha: number = 0;
  private static SCRIM_FADE_RATE = 6.0; // alpha units per second
  private static SCRIM_COLOR: [number, number, number, number] =
    [0.05, 0.04, 0.03, 0.75]; // warm-tinted near-black at 75% peak

  private active_pipeline: VideoRenderPipeline | null = null;
  private active_blob_pipeline: DebugBlobOverlayPipeline | null = null;
  private active: boolean = false;
  private activating=false;
  private activationGeneration=0;
  private buildContext:[GPUDevice,GPUTextureFormat,ShaderService,ResourceService]|null=null;
  private pipelineBuild:Promise<void>|null=null;
  private has_bound: boolean = false;

  // Stage-dependent fields are resolved in build() (which runs at scene
  // activation, after the Stage singleton exists) rather than in the
  // constructor, so StickerService can own/instantiate Video during Stage
  // construction without re-entrant Stage.instance() calls.
  private sticker_compute!: StickerCompute;
  private info: StickerInfoGpu3x3;
  private code_gate: StableCodeGate;
  private last_ineligible_code: string | null = null;
  private sticker_debug: StickerDebug;

  // The pipeline needs to rebuild its bind group whenever the bound texture
  // views change (i.e., after a pool publish promotes a new slot to Ready).
  private bound_slot_id: number = -1;

  private render_service!: RenderService;
  private capture!: CaptureService;
  private video_display_info: VideoDisplayInfo;

  // ---- Consumer-side telemetry (logging only) ---------------------------
  // Per-interval aggregate counters for the readback consumer path. Never hold
  // sticker payloads — only counts, sequences, and codes-as-strings.
  private tel_consumed = 0;
  private tel_valid_3x3 = 0;
  private tel_zero_code = 0;
  private tel_unique_codes = new Set<string>();
  private tel_last: Record<string, unknown> | null = null;

  constructor(key: string) {
    super(key);
    this.layer = 1;

    this.pipeline_nv12 = new VideoRenderPipeline('video_nv12', 'video_yuv_rgb_NV12');
    this.pipeline_i420 = new VideoRenderPipeline('video_i420', 'video_yuv_rgb_I420');
    this.debug_pipeline = new DebugTextureRenderPipeline('debug', 'debug_texture');
    this.blob_overlay_nv12 = new DebugBlobOverlayPipeline('blob_nv12', 'debug_blob_overlay_nv12');
    this.blob_overlay_i420 = new DebugBlobOverlayPipeline('blob_i420', 'debug_blob_overlay_i420');
    this.scan_feedback_pipeline = new ScanFeedbackPipeline('scan_feedback', 'scan_feedback');
    this.scrim_pipeline = new ScrimRenderPipeline('scrim', 'scrim');
    this.sticker_debug = new StickerDebug();
    this.code_gate = new StableCodeGate(6, 25, 10);

    // Stage-free: no Stage.instance() here. info is a pure CPU parser.
    this.info = new StickerInfoGpu3x3();
    this.video_display_info = { mode: 0, srcW: 0, srcH: 0, dstW: 0, dstH: 0 };
  }

  async build(
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    resource_service: ResourceService
  ): Promise<void> {
    // Resolve Stage-dependent services now (the Stage singleton is fully built
    // by scene-activation time). Kept out of the constructor so StickerService
    // can construct Video during Stage construction.
    const stage = Stage.instance();
    this.capture = stage.capture_service;
    this.render_service = stage.render_service;
    if(this.buildContext?.[0]!==device){
      this.pipelineBuild=null;
      this.sticker_compute = new StickerCompute(this.capture.camera_pool);
    }
    this.buildContext=[device,format,shader_service,resource_service];
    // No private shader fetch/compile on the opening scene's critical path.
    // Camera activation below waits explicitly, including a cold/failed warmup.
  }

  private ensurePipelines():Promise<void>{
    if(!this.buildContext)return Promise.reject(new Error("Scanner is not ready."));
    if(!this.pipelineBuild)this.pipelineBuild=(async()=>{
      const [device,format,shader_service,resource_service]=this.buildContext!;
      await shader_service.prepareVision();
      await this.pipeline_nv12.build(device, format, shader_service, resource_service);
      await this.pipeline_i420.build(device, format, shader_service, resource_service);
      await this.debug_pipeline.build(device, format, shader_service, resource_service);
      await this.blob_overlay_nv12.build(device, format, shader_service, resource_service);
      await this.blob_overlay_i420.build(device, format, shader_service, resource_service);
      await this.scan_feedback_pipeline.build(device, format, shader_service, resource_service);
      await this.scrim_pipeline.build(device, format, shader_service, resource_service);
    })().catch(error=>{this.pipelineBuild=null;throw error;});
    return this.pipelineBuild;
  }

  override ui_message(cmd: string, msg: string): void {
    super.ui_message(cmd, msg);
    if (cmd === 'scan_start') {
      void this.activate();
    } else if (cmd === 'scan_stop') {
      this.deactivate();
    } else if (cmd === 'scan_sticker') {
      // Sweet Tooth parity: a single message toggles the scanner.
      if (this.active||this.activating) this.deactivate();
      else void this.activate();
    } else if (cmd === 'render') {
      if (msg === 'color') this.show_mode = 'video';
      else if (msg === 'black and white') this.show_mode = 'mask';
      else if (msg === 'blobs') this.show_mode = 'blob_overlay';
    }
  }

  is_active(): boolean {
    return this.active;
  }

  private async activate(): Promise<void> {
    if (this.active||this.activating) return; // deterministic: never a second camera/instance
    if (!this.sticker_compute || !this.capture) return; // not built yet

    this.sticker_compute.cpu_pool.clear();
    this.code_gate.clear();
    this.last_ineligible_code = null;
    this.activating=true;
    const generation=++this.activationGeneration;

    try {
      await this.ensurePipelines();
      if(generation!==this.activationGeneration)return;
      await this.capture.start();
      if(generation!==this.activationGeneration){this.capture.stop();return;}
      this.sticker_compute.enable();
      this.active = true;
      this.has_bound = false;
      this.bound_slot_id = -1;
      rb_debug_log('[video] capture started, overlay active');
    } catch (err) {
      if(generation!==this.activationGeneration)return;
      this.active = false;
      this.capture.stop();
      const message = err instanceof Error ? err.message : String(err);
      // The scanner failed to start (permission denied, no device, etc.). Settle
      // the awaiting scan as a typed error through the bridge.
      scanBridge.report_error(new StickerError('scan_failed', message));
    } finally {
      if(generation===this.activationGeneration)this.activating=false;
    }
  }

  private deactivate(): void {
    ++this.activationGeneration;this.activating=false;
    if (!this.active) return;
    this.sticker_compute.cpu_pool.clear();
    this.code_gate.clear();
    this.sticker_compute.disable();
    this.capture.stop();
    this.active = false;
    this.has_bound = false;
    this.active_pipeline = null;
    this.active_blob_pipeline = null;
    this.bound_slot_id = -1;
    rb_debug_log('[video] capture stopped, overlay hidden');
  }

  private get_orientation_type():
    | 'portrait-primary'
    | 'portrait-secondary'
    | 'landscape-primary'
    | 'landscape-secondary'
    | 'unknown' {
    const type = window.screen?.orientation?.type;
    if (
      type === 'portrait-primary' ||
      type === 'portrait-secondary' ||
      type === 'landscape-primary' ||
      type === 'landscape-secondary'
    ) {
      return type;
    }
    const angle =
      window.screen?.orientation?.angle ??
      (typeof (window as unknown as { orientation?: number }).orientation === 'number'
        ? (window as unknown as { orientation: number }).orientation
        : null);
    if (angle === 0) return 'portrait-primary';
    if (angle === 180) return 'portrait-secondary';
    if (angle === 90) return 'landscape-primary';
    if (angle === -90 || angle === 270) return 'landscape-secondary';
    return 'unknown';
  }

  private detect_ios_handheld_or_tablet(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const maxTouchPoints = navigator.maxTouchPoints || 0;
    const isIPhoneOrIPod = /iPhone|iPod/.test(ua) || /iPhone|iPod/.test(platform);
    const isIPad =
      /iPad/.test(ua) ||
      /iPad/.test(platform) ||
      (platform === 'MacIntel' && maxTouchPoints > 1);
    return isIPhoneOrIPod || isIPad;
  }

  private update_video_display_info(): void {
    if (!this.render_service.canvas) return;
    const cam = this.capture.camera_info;
    const isAppleMobile = this.detect_ios_handheld_or_tablet();
    const dstW = this.render_service.canvas.width;
    const dstH = this.render_service.canvas.height;
    const orient = this.get_orientation_type();

    let mode = 0;
    if (cam.facing === 'front') {
      mode = 1;
    } else if (isAppleMobile) {
      if (orient === 'portrait-primary') mode = 2;
      else if (orient === 'landscape-primary') mode = 0;
      else if (orient === 'landscape-secondary') mode = 3;
      else if (orient === 'portrait-secondary') mode = 4;
    }

    this.video_display_info = {
      mode,
      srcW: this.sticker_compute.debug_width,
      srcH: this.sticker_compute.debug_height,
      dstW,
      dstH,
    };
  }

  override tick(delta_time: number): void {
    super.tick(delta_time);

    // Scrim fade runs regardless of active state so it can also fade back out
    // after the scanner is deactivated. Target follows the active flag.
    const scrim_target = this.active ? Video.SCRIM_COLOR[3] : 0;
    const step = Video.SCRIM_FADE_RATE * delta_time;
    if (this.scrim_alpha < scrim_target) {
      this.scrim_alpha = Math.min(scrim_target, this.scrim_alpha + step);
    } else if (this.scrim_alpha > scrim_target) {
      this.scrim_alpha = Math.max(scrim_target, this.scrim_alpha - step);
    }

    if (!this.active) return;
    this.update_video_display_info();

    const cpu_pool = this.sticker_compute.cpu_pool;
    const slot = cpu_pool.peek_latest_ready_slot();
    if (!slot) {
      rb_debug_log('[video tick] null slot');
    }
    if (!slot || !slot.mapped_sticker_data || !slot.mapped_blob_data) return;

    const sticker_bytes = slot.mapped_sticker_data;
    const sticker_words = new Uint32Array(
      sticker_bytes.buffer,
      sticker_bytes.byteOffset,
      sticker_bytes.byteLength >>> 2
    );

    const blob_bytes = slot.mapped_blob_data;
    const blob_words = new Uint32Array(
      blob_bytes.buffer,
      blob_bytes.byteOffset,
      blob_bytes.byteLength >>> 2
    );

    // Optional on-device debug dump (mirrors Sweet Tooth): key 0 prints the
    // parsed sticker/blob tables to the console.
    const input = Stage.instance().input_service;
    if (input.was_key_pressed(Keys.Digit0)) {
      this.sticker_debug.print_stickers(sticker_words, blob_words);
    }

    // Telemetry: this is one consumed CPU-ready result.
    const source_seq = slot.source_gpu_sequence;
    this.tel_consumed++;
    let total_parsed = 0;
    let candidates_3x3 = 0;
    let last_pushed_code: bigint = 0n;
    let last_result: CodeGateResult | null = null;
    let accepted = false;

    // Parse the sticker results from the mapped buffer. On the first stabilized
    // 3x3 code, report it through the bridge and stop scanning.
    for (let i = 0; i < MAX_STICKERS; i++) {
      const type = this.info.get_sticker_type(i, sticker_words);
      if (type !== STICKER_TYPE_INVALID) total_parsed++;
      if (type !== STICKER_TYPE_3X3) continue;

      const code = this.info.get_code(i, sticker_words);
      if (code <= 0n) continue;

      candidates_3x3++;
      this.tel_unique_codes.add(code.toString());
      const result = this.code_gate.push(code);
      last_pushed_code = code;
      last_result = result;
      if (!result.ok) continue;

      const accepted_code = result.acceptedCode ?? code;
      const code_str = accepted_code.toString();
      // Membership is checked only AFTER the unchanged confidence gate. An
      // unused/misread proof sticker must not close the camera or consume the
      // pending link; wait for a genuinely recognized saved member instead.
      if (!scanBridge.accepts_code(code_str)) {
        this.code_gate.clear();
        if (this.last_ineligible_code !== code_str) {
          this.last_ineligible_code = code_str;
          Stage.instance().footer(
            `Read sticker ${code_str}; scan an already-collected robot to finish adding.`, SCAN_FOOTER_SLOT);
          console.info('[StickerService] rejected collection proof candidate', { code: code_str });
        }
        continue;
      }
      accepted = true;

      // Emit acceptance immediately (unthrottled) BEFORE the pools/gate are
      // cleared, so the accepting frame is always captured.
      rb_log('gate_accept', {
        src_seq: source_seq,
        total_parsed,
        candidates_3x3,
        code: code.toString(),
        accepted_code: code_str,
        reason: result.reason,
        consecutive: result.consecutiveCount,
        window_votes: result.bestWindowVotes,
        window_size: result.windowSize,
      });
      rb_debug_log('3x3 code accepted:', code_str, result.reason);
      this.log_gate_acceptance_to_console(
        accepted_code,
        result,
        i,
        code,
        sticker_words,
        blob_words
      );

      // Echo Engine deviation: tear down first so the camera/pipeline is off
      // before the result is observed, then report the code back to the
      // awaiting adapter through the ScanBridge (Sweet Tooth instead re-emits
      // the code onto the scene bus). The scan lifecycle is identical.
      this.deactivate();
      Stage.instance().footer(`Scanned sticker ${code_str}`, SCAN_FOOTER_SLOT);
      scanBridge.report_scanned(code_str);
      return;
    }

    // Consumer-side telemetry aggregation. Acceptance is logged above; here we
    // only emit throttled samples/rates to avoid per-frame console spam.
    if (candidates_3x3 > 0) this.tel_valid_3x3++;
    else this.tel_zero_code++;

    const gate = this.code_gate.snapshot();
    this.tel_last = {
      src_seq: source_seq,
      total_parsed,
      candidates_3x3,
      code: last_pushed_code.toString(),
      accepted,
      reason: last_result ? last_result.reason : 'no_candidate',
      consecutive: gate.consecutiveCount,
      required_consecutive: gate.requiredConsecutive,
      window_votes: gate.bestWindowVotes,
      window_best: gate.bestWindowCode !== null ? gate.bestWindowCode.toString() : null,
      window_len: gate.historyLength,
      window_size: gate.windowSize,
      min_window_votes: gate.minWindowVotes,
    };

    if (rb_throttle('gate_sample')) {
      rb_log('gate_sample', this.tel_last);
    }
    this.log_consumer_rates();
  }

  // Logging only: once/interval, emit consumer-path aggregate counters, then
  // reset them for the next interval. Bounded unique-code list.
  private log_consumer_rates(): void {
    if (!rb_throttle('gate_rates')) return;

    const codes = Array.from(this.tel_unique_codes);
    rb_log('gate_rates', {
      consumed: this.tel_consumed,
      valid_3x3: this.tel_valid_3x3,
      zero_code: this.tel_zero_code,
      unique_codes: codes.length,
      codes: codes.slice(0, 8),
    });

    this.tel_consumed = 0;
    this.tel_valid_3x3 = 0;
    this.tel_zero_code = 0;
    this.tel_unique_codes.clear();
  }

  private format_gate_candidate(candidate: CodeGateCandidateStats): string {
    const parts = [
      candidate.code.toString(),
      `n${candidate.count}`,
      `age${candidate.lastSeenFramesAgo}`,
    ];
    if (candidate.currentStreak > 0) parts.push(`st${candidate.currentStreak}`);
    if (candidate.isBestWindow) parts.push('best');
    if (candidate.isAccepted) parts.push('ACCEPT');
    return parts.join(' ');
  }

  private log_gate_acceptance_to_console(
    acceptedCode: bigint,
    result: CodeGateResult,
    triggerIndex: number,
    triggerCode: bigint,
    stickerWords: Uint32Array,
    blobWords: Uint32Array
  ): void {
    const gate = this.code_gate.snapshot(acceptedCode);
    const candidates = gate.candidates.map((candidate) => this.format_gate_candidate(candidate));
    const console_service = Stage.instance().console_service;
    console_service.log(
      [
        `[gate] accept ${acceptedCode.toString()}`,
        `why=${result.reason}`,
        `cons=${gate.consecutiveCount}/${gate.requiredConsecutive}`,
        `win=${gate.bestWindowVotes}/${gate.historyLength}`,
        `cfg=${gate.windowSize}/${gate.minWindowVotes}`,
        `rej=${gate.rejectedCount}`,
        `exp=${gate.expiredCount}`,
        `c=[${candidates.join(' | ')}]`,
      ].join(' ')
    );

    try {
      const diagnostic = buildAcceptedStickerDiagnosticLog({
        acceptedCode,
        triggerCode,
        triggerIndex,
        reason: result.reason,
        stickerWords,
        blobWords,
      });
      if (diagnostic) {
        console_service.log(diagnostic);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console_service.log(`[scan3x3] diagnostic_failed ${message}`);
    }
  }

  override prepare(device: GPUDevice, _camera: Camera): void {
    // Scrim is prepared/drawn independently of the scanner-active gate so its
    // fade-out still renders after deactivate. Runs BEFORE the video setup.
    if (this.scrim_alpha > 0.001) {
      this.scrim_pipeline.update_color(
        device,
        Video.SCRIM_COLOR[0],
        Video.SCRIM_COLOR[1],
        Video.SCRIM_COLOR[2],
        this.scrim_alpha
      );
      this.scrim_pipeline.prepare(device);
    }

    if (
      !this.active ||
      !this.render_service ||
      !this.render_service.canvas ||
      !this.sticker_compute ||
      !this.sticker_compute.active_gpu_slot
    ) {
      return;
    }

    this.scan_feedback_pipeline.prepare(
      device,
      this.sticker_compute.active_gpu_slot,
      this.video_display_info
    );

    if (this.show_mode === 'blob_overlay' && this.sticker_compute.debug_blob_view) {
      const cam_slot = this.capture.camera_pool.peek_latest_ready_slot();
      if (!cam_slot) return;

      const active_blob =
        cam_slot.format === 'I420' ? this.blob_overlay_i420 : this.blob_overlay_nv12;

      active_blob.update_params(
        device,
        this.video_display_info.mode,
        this.sticker_compute.debug_width,
        this.sticker_compute.debug_height,
        this.video_display_info.dstW,
        this.video_display_info.dstH
      );
      active_blob.bind_textures(device, cam_slot, this.sticker_compute.debug_blob_view);
      this.active_blob_pipeline = active_blob;
    }

    if (this.show_mode === 'mask' && this.sticker_compute.debug_mask_view) {
      this.debug_pipeline.update_params(
        device,
        this.video_display_info.mode,
        this.sticker_compute.debug_width,
        this.sticker_compute.debug_height,
        this.video_display_info.dstW,
        this.video_display_info.dstH
      );
      this.debug_pipeline.bind_texture(device, this.sticker_compute.debug_mask_view);
      return;
    }

    const slot = this.capture.camera_pool.peek_latest_ready_slot();
    if (!slot) return;

    this.active_pipeline =
      slot.format === 'I420' ? this.pipeline_i420 : this.pipeline_nv12;

    this.active_pipeline.update_params(
      device,
      this.video_display_info.mode,
      slot.y_width,
      slot.y_height,
      this.video_display_info.dstW,
      this.video_display_info.dstH
    );
    this.active_pipeline.bind_slot(device, slot);
    this.active_pipeline.prepare(device);
    this.has_bound = true;
  }

  override compute(
    device: GPUDevice,
    pass: GPUComputePassEncoder,
    encoder: GPUCommandEncoder
  ): void {
    if (!this.active) return;
    this.sticker_compute.dispatch_frame(device, pass, encoder, this.video_display_info);
  }

  override readback_encode(device: GPUDevice, encoder: GPUCommandEncoder): void {
    if (!this.active) return;
    this.sticker_compute.encode_readback(encoder);
  }

  override draw(pass: GPURenderPassEncoder): void {
    // Scrim draws FIRST — the dim backdrop sits behind the camera feed and every
    // video overlay/feedback layer. Drawn outside the active gate so its
    // fade-out still renders after the scanner stops.
    if (this.scrim_alpha > 0.001) {
      this.scrim_pipeline.draw(pass);
    }

    if (!this.active) return;

    switch (this.show_mode) {
      case 'video':
        if (!this.has_bound || !this.active_pipeline) return;
        this.active_pipeline.draw(pass);
        this.scan_feedback_pipeline.draw(pass);
        break;
      case 'mask':
        this.debug_pipeline.draw(pass);
        this.scan_feedback_pipeline.draw(pass);
        break;
      case 'blob_overlay':
        if (this.active_blob_pipeline) {
          this.scan_feedback_pipeline.draw(pass);
          this.active_blob_pipeline.draw(pass);
        } else if (this.has_bound && this.active_pipeline) {
          this.active_pipeline.draw(pass);
          this.scan_feedback_pipeline.draw(pass);
        }
        break;
    }
  }

  override readback_poll(device: GPUDevice): void {
    if (!this.active) return;
    this.sticker_compute.after_submit(device);
  }
}
